import { Link, Outlet, useLocation } from "react-router";
import {
  BellRing,
  Home,
  Users,
  ListTodo,
  FileText,
  FilePen,
  BarChart3,
  Menu,
  X,
  TrendingUp,
  ShoppingCart,
  Clock,
  BookOpen,
  Truck,
  Megaphone,
  Briefcase,
  Factory,
  ClipboardList,
  AlertCircle,
  ChevronDown,
  Flag,
  FlaskConical,
  ShieldCheck,
  Target,
  Gift,
  Package,
  UserCog,
  Receipt,
  Banknote,
  Building2,
  Landmark,
  ListChecks,
  PackageCheck,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./primitives/dropdown-menu";
import { Toaster } from "./primitives/sonner";
import { FeedbackWidget } from "./domain/feedback/FeedbackWidget";
import { Logo } from "./Logo";
import { AccountMenu } from "./AccountMenu";
import { NotificationBell } from "./domain/notifications/NotificationBell";
import { useState, useEffect } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { subscribeToTicketsChanged } from "@shared-core/domain/ticketEvents";
import {
  useAuth,
  useCanManageAccountAllocation,
  useCanReadForecasting,
  useCanUseJourneyTesting,
  useCanViewStock,
} from "../auth/hooks";
import {
  isRouteEnabled,
  paths,
  type RouteId,
} from "@shared-core/routing/routes";

export function DashboardLayout() {
  const location = useLocation();
  const dataAdapter = useDataAdapter();
  const { user } = useAuth();
  const canReadForecasting = useCanReadForecasting();
  const canViewStock = useCanViewStock();
  const canManageAccountAllocation = useCanManageAccountAllocation();
  const canUseJourneyTesting = useCanUseJourneyTesting();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ticketCount, setTicketCount] = useState(0);

  // Load the signed-in user's open-ticket count for the badge — server-side
  // filter so the client just reads the total from ListResponse.
  //
  // Fetched on mount/user change AND whenever a page reports a ticket
  // mutation via ticketEvents (there's no shared query cache in this app,
  // so this small pub/sub is the proportionate stand-in — see
  // packages/shared-core/src/domain/ticketEvents.ts). Not keyed on
  // location.pathname: that re-ran a full COUNT(*) on every route change,
  // the single most frequently fired request in production.
  const currentUserId = user?.id;
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    async function loadTicketCount() {
      try {
        const response = await dataAdapter.getTickets({
          assignedToUserId: currentUserId,
          status: "open",
          pageSize: 1,
        });
        if (!cancelled) setTicketCount(response.total);
      } catch (error) {
        console.error("Error loading ticket count:", error);
      }
    }
    loadTicketCount();
    const unsubscribe = subscribeToTicketsChanged(loadTicketCount);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [dataAdapter, currentUserId]);

  type NavLeaf = {
    /** The manifest entry this leaf points at — also decides whether it shows. */
    route: RouteId;
    path: string;
    label: string;
    /** Match the pathname exactly rather than by prefix. Needed by any leaf
     *  whose path is "/", which would otherwise prefix-match every route. */
    exact?: boolean;
    icon: typeof Home;
    badge?: number;
    /** Hidden unless `useCanManageAccountAllocation()` (manager/admin) — a
     *  role gate on top of the feature flag, same shape as
     *  WorkspaceNav.tsx's forecasting-targets leaf. */
    managerOnly?: boolean;
    /** Shown only to the roles holding journey_testing:use (the test console). */
    journeyTestingOnly?: boolean;
  };

  type NavGroup = {
    key: string;
    label: string;
    icon: typeof Home;
    route?: RouteId;
    path?: string;
    children?: NavLeaf[];
  };

  // Grouped by print-lifecycle stage rather than by feature name, so new
  // stages (dispatch, invoicing) have an obvious home as the product grows.
  // Settings lives in the account menu, not here — see AccountMenu.
  //
  // Because the groups are lifecycle-shaped they CUT ACROSS features: Sales
  // holds Customers and Estimates (live in production) beside Catalogue and
  // Plan (not). So the filter below runs at the LEAF level, and a group is
  // dropped only once every one of its leaves has gone.
  const allNavGroups: NavGroup[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: Home,
      // Prepress sits here rather than under Production: it is a standing team
      // board people work out of all day (safe-to-print gate, blocked runs,
      // artwork triage), not a per-job production surface like Jobs or
      // Tracking. Each leaf keeps its own feature gate, so a user who holds
      // only one of the two sees a group with only that one in it.
      children: [
        {
          route: "root",
          path: paths.root(),
          label: "Overview",
          icon: Home,
          exact: true,
        },
        {
          route: "prepressHome",
          path: paths.prepressHome(),
          label: "Prepress",
          icon: ShieldCheck,
        },
      ],
    },
    {
      key: "sales",
      label: "Sales",
      icon: Briefcase,
      children: [
        {
          route: "customers",
          path: paths.customers(),
          label: "Customers",
          icon: Users,
        },
        {
          route: "leads",
          path: paths.leads(),
          label: "Leads",
          icon: Target,
        },
        {
          route: "catalogue",
          path: paths.catalogue(),
          label: "Catalogue",
          icon: BookOpen,
        },
        {
          route: "estimates",
          path: paths.estimates(),
          label: "Estimates",
          icon: FileText,
        },
        // CIS quotes (`QT-`), beside Tharstern's estimates.
        {
          route: "quotes",
          path: paths.quotes(),
          label: "Quotes",
          icon: FilePen,
        },
        {
          route: "promoOffice",
          path: paths.promoOffice(),
          label: "Promo Office",
          icon: Gift,
        },
        // One entry, two destinations. The forecast grid behind /plan is
        // gated on FORECASTING_ALL, which a CSR does not hold — landing them
        // there is a 403. My Pipeline is the part of the workspace they can
        // actually use (four CSRs own a sales-rep portfolio), so that is
        // where the entry takes them. Feature gating still decides whether
        // the entry exists at all; this only decides where it points.
        {
          route: "plan",
          path: canReadForecasting ? paths.plan() : paths.myPipeline(),
          label: "Plan",
          icon: TrendingUp,
        },
      ],
    },
    {
      key: "production",
      label: "Production",
      icon: Factory,
      // The standalone "Booking" entry is gone — Ops view *is* the booking
      // workspace, reached via the Sales | Ops toggle inside Jobs. See
      // SalesOpsToggle.
      children: [
        // Two tracks: CIS orders, and Tharstern's jobs made in-house.
        {
          route: "orders",
          path: paths.orders(),
          label: "Orders",
          icon: ClipboardList,
        },
        // Needs attention (v1 completion I §10): what on orders and purchase
        // orders a person should act on.
        {
          route: "needsAttention",
          path: paths.needsAttention(),
          label: "Needs attention",
          icon: BellRing,
        },
        // Production › Purchasing: purchase orders to place and following
        // them in (order to invoice doc 57 §3). Finance › Purchase orders
        // keeps the money.
        {
          route: "purchasing",
          path: paths.purchasing(),
          label: "Purchasing",
          icon: PackageCheck,
        },
        {
          route: "jobs",
          path: paths.jobs(),
          label: "Jobs",
          icon: ShoppingCart,
        },
        {
          route: "tracking",
          path: paths.tracking(),
          label: "Tracking",
          icon: Truck,
        },
        // Every staff role but engineer can view stock (Permit.STOCK_READ),
        // so the leaf is hidden rather than left as a link that bounces.
        ...(canViewStock
          ? [
              {
                route: "stock" as const,
                path: paths.stock(),
                label: "Stock",
                icon: Package,
              },
            ]
          : []),
      ],
    },
    {
      // The commercial-oversight surface (exceptions board now; the wider
      // cockpit later). Deliberately its own top-level entry, not a Sales
      // leaf — it is a job-group tool (commercial department), not part of
      // the rep/CSR selling journey, and access narrows to that group.
      key: "commercial",
      label: "Commercial",
      icon: Flag,
      children: [
        {
          route: "commercialExceptions",
          path: paths.commercialExceptions(),
          label: "Exceptions",
          icon: Flag,
        },
        {
          route: "carriageCosts",
          path: paths.carriageCosts(),
          label: "Carriage",
          icon: Truck,
        },
      ],
    },
    {
      // The invoicing stage the grouping comment above anticipates: the two
      // halves of one purchase ledger (invoices waiting to be matched, orders
      // still waiting for an invoice) and the suppliers they belong to.
      key: "finance",
      label: "Finance",
      icon: Receipt,
      children: [
        {
          route: "purchaseInvoices",
          path: paths.objectList({
            objectId: "finance_purchase_ledger.purchase_invoice",
          }),
          label: "Purchase invoices",
          icon: Receipt,
        },
        {
          route: "purchaseOrders",
          path: paths.purchaseOrders(),
          label: "Purchase orders",
          icon: ClipboardList,
        },
        {
          route: "suppliers",
          path: paths.suppliers(),
          label: "Suppliers",
          icon: Building2,
        },
        // The sales ledger's pro-formas still asking for money (doc 50 §9
        // item 5): where finance records a bank transfer.
        {
          route: "financeAwaitingPayment",
          path: paths.financeAwaitingPayment(),
          label: "Awaiting payment",
          icon: Banknote,
        },
        // What each payer owes by days overdue, and the disputes on it (credit control L).
        {
          route: "financeAgedDebt",
          path: paths.financeAgedDebt(),
          label: "Aged debt",
          icon: Clock,
        },
        // Payers' credit accounts, stops and holds (Ben, 9 Oct: under Finance).
        {
          route: "creditControl",
          path: paths.creditControl(),
          label: "Credit control",
          icon: Landmark,
        },
        // How a purchase invoice line is coded: last in the menu (Ben, 3 Oct).
        {
          route: "financeLedgerRules",
          path: paths.financeLedgerRules(),
          label: "Ledger rules",
          icon: ListChecks,
        },
      ],
    },
    {
      key: "marketing",
      route: "marketingCampaigns",
      path: paths.marketingCampaigns(),
      label: "Marketing",
      icon: Megaphone,
    },
    {
      key: "insights",
      route: "insights",
      path: paths.insights(),
      label: "Insights",
      icon: BarChart3,
    },
    {
      key: "workspace",
      label: "Workspace",
      icon: ClipboardList,
      children: [
        {
          route: "tickets",
          path: paths.tickets(),
          label: "Tickets",
          icon: ListTodo,
          badge: ticketCount,
        },
        {
          route: "issues",
          path: paths.issues(),
          label: "Issues",
          icon: AlertCircle,
        },
        {
          route: "accountAllocation",
          path: paths.accountAllocation(),
          label: "Account allocation",
          icon: UserCog,
          managerOnly: true,
        },
        {
          route: "journeyTesting",
          path: paths.journeyTestingHome(),
          label: "Test console",
          icon: FlaskConical,
          journeyTestingOnly: true,
        },
      ],
    },
  ];

  // Drop what this user cannot reach. A leaf whose feature is off would be a
  // dead-end link into the not-found page; a group left with no leaves would
  // be an empty dropdown.
  const navGroups: NavGroup[] = allNavGroups
    .map((group) =>
      group.children
        ? {
            ...group,
            children: group.children.filter(
              (c) =>
                isRouteEnabled(c.route) &&
                (!c.managerOnly || canManageAccountAllocation) &&
                (!c.journeyTestingOnly || canUseJourneyTesting),
            ),
          }
        : group,
    )
    .filter((group) =>
      group.children ? group.children.length > 0 : isRouteEnabled(group.route!),
    );

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    // The Plan workspace owns its inner-tab sub-routes (/plan/*).
    if (path === paths.plan()) {
      return location.pathname.startsWith("/plan");
    }
    // The Marketing entry owns the whole /marketing/* workspace
    // (campaigns/audiences/consent/results).
    if (path === paths.marketingCampaigns()) {
      return location.pathname.startsWith("/marketing");
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (group: NavGroup) => {
    if (group.path) {
      return isActive(group.path);
    }
    return (group.children ?? []).some((child) =>
      isActive(child.path, child.exact),
    );
  };

  // Full-bleed mode for feature workspace (split-pane needs full width)
  const isFeatureWorkspace = /^\/features\/[^/]/.test(location.pathname);

  // Close mobile menu when route changes
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="border-b border-border bg-surface">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between py-4 sm:py-6">
            {/* Logo - left aligned */}
            <div className="flex-shrink-0">
              <Link to={paths.root()}>
                <Logo variant="full-color" size="lg" showTagline />
              </Link>
            </div>

            {/* Desktop Navigation - right side. Group icons show only from 2xl:
                between xl and 2xl the row of groups is wider than the space
                beside the logo, and without the icons it fits with room for
                the groups still to come. */}
            <div className="hidden xl:flex items-center gap-1">
              {navGroups.map((group) => {
                const Icon = group.icon;
                const active = isGroupActive(group);
                const linkClassName = `
                  flex items-center gap-2 px-3 py-2 rounded-md transition-colors
                  ${
                    active
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                  }
                `;

                if (!group.children) {
                  return (
                    <Link
                      key={group.key}
                      to={group.path!}
                      className={linkClassName}
                    >
                      <Icon className="hidden size-4 2xl:block" />
                      <span>{group.label}</span>
                    </Link>
                  );
                }

                const groupBadge = group.children.reduce(
                  (total, child) => total + (child.badge ?? 0),
                  0,
                );

                return (
                  <DropdownMenu key={group.key}>
                    <DropdownMenuTrigger className={linkClassName}>
                      <Icon className="hidden size-4 2xl:block" />
                      <span>{group.label}</span>
                      {groupBadge > 0 && (
                        <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                          {groupBadge}
                        </span>
                      )}
                      <ChevronDown className="size-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {group.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <DropdownMenuItem key={child.path} asChild>
                            <Link to={child.path}>
                              <ChildIcon className="size-4" />
                              <span>{child.label}</span>
                              {!!child.badge && (
                                <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                                  {child.badge}
                                </span>
                              )}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
              <div className="ml-2 flex items-center gap-1">
                <NotificationBell />
                <AccountMenu />
              </div>
            </div>

            {/* Mobile: account menu + hamburger */}
            <div className="xl:hidden flex items-center gap-2">
              <NotificationBell />
              <AccountMenu />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md hover:bg-surface-hover text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="size-6" />
                ) : (
                  <Menu className="size-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Dropdown */}
          {mobileMenuOpen && (
            <div className="xl:hidden pb-4 space-y-1">
              {navGroups.map((group) => {
                if (!group.children) {
                  const Icon = group.icon;
                  const active = isGroupActive(group);
                  return (
                    <Link
                      key={group.key}
                      to={group.path!}
                      onClick={handleNavClick}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                        ${
                          active
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                        }
                      `}
                    >
                      <Icon className="size-5" />
                      <span className="font-medium">{group.label}</span>
                    </Link>
                  );
                }

                return (
                  <div key={group.key}>
                    <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </p>
                    {group.children.map((child) => {
                      const Icon = child.icon;
                      const active = isActive(child.path, child.exact);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={handleNavClick}
                          className={`
                            flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                            ${
                              active
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                            }
                          `}
                        >
                          <Icon className="size-5" />
                          <span className="font-medium">{child.label}</span>
                          {!!child.badge && (
                            <span className="ml-1 text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content. The bottom padding (pb-24) clears the fixed Feedback
          button (bottom-6 + h-12), so a page's last actions can always be
          scrolled above it rather than sitting underneath it. */}
      <main
        className={
          isFeatureWorkspace
            ? "px-4 sm:px-6 pt-4 pb-24"
            : "max-w-[1400px] mx-auto px-4 sm:px-8 pt-6 sm:pt-8 pb-24"
        }
      >
        {user?.role === "pending" ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <Clock className="h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              Account awaiting activation
            </h2>
            <p className="text-muted-foreground max-w-md">
              Your account has been created but is not yet active. An
              administrator will assign you a role and portfolio shortly.
            </p>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Toast Notifications */}
      <Toaster />

      {/* Always-on feedback widget */}
      <FeedbackWidget />
    </div>
  );
}
